import { ChangePasswordError } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import { MyVaroContext } from 'features/myVaro/provider/MyVaroContextProvider';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Keyboard,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ChangePasswordInitiate from 'services/graphql/mutations/ChangePasswordInitiate';
import Button from 'shared/components/vxl/Button/Button';
import { passwordSchema } from 'shared/components/vxl/input/schemas/password.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isAndroid, isWeb } from 'shared/constants/platform';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import theme from 'shared/styles/theme';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface ChangePasswordResponse extends ChangePasswordError {
  customerInfo: {
    phone: string;
  };
}
interface ChangePasswordInitiateResponse {
  changePasswordInitiate: ChangePasswordResponse;
}

const ChangePasswordScreen = () => {
  const router = useRouter();
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'changePassword',
  });
  const { setPassword, setLastFourPhoneDigits } = useContext(MyVaroContext);
  const insets = useSafeAreaInsets();

  const [mutationResult, executeMutation] =
    useCancellableMutation<ChangePasswordInitiateResponse>(
      ChangePasswordInitiate,
    );

  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const { fetching: mutationFetching } = mutationResult;
  const passwordMaskPressed = usePressableAnalytics({
    analyticsId: 'passwordMaskClicked',
  });

  const confirmCurrentPassword = async () => {
    try {
      const response = await executeMutation({ input: { currentPassword } });
      const responseData = response?.data?.changePasswordInitiate;

      if (responseData?.code === 'success') {
        setPassword?.(currentPassword);
        const phone = responseData.customerInfo?.phone ?? '';
        setLastFourPhoneDigits?.(phone.slice(-4));
        router.navigate('/myvaro/personal-details/change-password/enter-code');
      } else {
        setPasswordError(responseData?.message ?? '');
      }
    } catch (error) {
      logErrorWithContext(error, {
        featureArea: FeatureArea.CHANGE_PASSWORD,
        responsibleTeam: ResponsibleTeam.FRAUD,
        severity: ErrorSeverity.LOW,
      });
    }
  };

  const disableButton = !passwordSchema.safeParse(currentPassword).success;

  return (
    <TouchableWithoutFeedback
      accessible={false}
      onPress={() => {
        if (!isWeb) {
          Keyboard.dismiss();
        }
      }}
    >
      <View style={styles.container}>
        <Stack.Screen
          options={{
            headerShown: true,
            header: (props: NativeStackHeaderProps) => (
              <ExpoNavigationBar
                {...props}
                options={{
                  headerBackVisible: true,
                  title: translatedText('title'),
                }}
              />
            ),
          }}
        />
        <View>
          <BodyBaseText numberOfLines={0} style={styles.currentPassLabel}>
            {translatedText('labels.editDescription')}
          </BodyBaseText>
          <VXLTextInput
            value={currentPassword}
            textContentType="password"
            onChangeText={(text) => {
              setCurrentPassword(text);
              setPasswordError('');
            }}
            onSubmitEditing={confirmCurrentPassword}
            errorText={passwordError}
            labelText={translatedText('labels.inputLabel')}
            secureTextEntry={
              !isPasswordVisible && (isAndroid ? currentPassword !== '' : true)
            }
            autoComplete="password"
            importantForAutofill="yes"
            rightIcon={{
              iconKey: !isPasswordVisible ? 'VIEW' : 'HIDE',
              iconAccessibilityLabel: isPasswordVisible
                ? 'eye-close'
                : 'eye-open',
              onPress: () => {
                passwordMaskPressed();
                setPasswordVisible(!isPasswordVisible);
              },
            }}
            testID="text-input-currentPassword"
          />
        </View>
        <View style={{ paddingBottom: insets.bottom + theme.spacing.m }}>
          <Button
            onPress={confirmCurrentPassword}
            isDisabled={disableButton}
            analyticsId="currentPasswordSubmitted"
            label={translatedText('button.next')}
            isLoading={mutationFetching}
          />
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
  },
  currentPassLabel: {
    marginVertical: theme.spacing.m,
  },
});

export default ChangePasswordScreen;
